import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/lib/media";
import { YoutubeCard } from "@/components/YoutubeCard";
import { CTASection } from "@/components/CTASection";
import { ArrowLeft, CalendarDays, MapPin } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((res) => setPost(res.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-post-not-found" className="pt-40 pb-24 px-5 text-center">
        <p className="font-heading text-2xl mb-4">Post not found.</p>
        <Link to="/blog" className="text-accent hover:underline">Back to Blog</Link>
      </div>
    );
  }

  if (!post) return <div className="pt-40 pb-24 text-center text-muted-foreground">Loading…</div>;

  const visibleCtas = (post.ctas || []).filter((c) => c.visible);

  return (
    <article data-testid="blog-post-page" className="pt-32 md:pt-40 pb-24">
      <SEO title={post.title} description={post.excerpt || post.title} path={`/blog/${post.slug}`} />
      <div className="max-w-3xl mx-auto px-5 md:px-10">
        <Link to="/blog" data-testid="blog-post-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>
        <h1 className="font-heading text-3xl md:text-5xl tracking-tight mb-4">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
          <span>By {post.author}</span>
          {post.event_date && <span className="flex items-center gap-1.5 text-accent"><CalendarDays className="h-4 w-4" />{post.event_date}</span>}
          {post.event_location && <span className="flex items-center gap-1.5 text-accent"><MapPin className="h-4 w-4" />{post.event_location}</span>}
        </div>
        {post.featured_image && (
          <div className="aspect-[16/9] overflow-hidden border border-border mb-10">
            <img src={resolveImageUrl(post.featured_image)} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="prose prose-brand max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />

        {post.gallery_images?.length > 0 && (
          <div data-testid="blog-post-gallery" className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-12">
            {post.gallery_images.map((img, i) => (
              <div key={i} className="aspect-square overflow-hidden border border-border">
                <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {post.youtube_videos?.length > 0 && (
          <div data-testid="blog-post-videos" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12">
            {post.youtube_videos.map((id, i) => (
              <YoutubeCard key={i} youtubeId={id} title={post.title} />
            ))}
          </div>
        )}
      </div>

      {visibleCtas.length > 0 && (
        <div data-testid="blog-post-ctas" className="mt-16 space-y-1">
          {visibleCtas.map((cta) => <CTASection key={cta.id} cta={cta} />)}
        </div>
      )}
    </article>
  );
}
